import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSignup } from '../../hooks/useSignup';
import { COMPANY_URL } from '../../config/configs';
import { AppRoutes } from '../../routes';

import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Checkbox,
  Container,
  FormControlLabel,
  Grid,
  Link,
  TextField,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';

const Signup: React.FC = () => {
  const [firstName, setFirstName] = useState<string>('');
  const [lastName, setLastName] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [disabledSubmit, setDisableSubmit] = useState<boolean>(true);
  const [agreeToTerms, setAgreeToTerms] = useState<boolean>(false);

  const { signup, loading, error, success, setError } = useSignup();
  const navigate = useNavigate();

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    try {
      await signup(email, password, firstName, lastName);
    } catch (error: any) {
      setAgreeToTerms(false);
      setDisableSubmit(true);
    }
  };

  function Copyright(props: any) {
    return (
      <Typography
        variant="body2"
        color="text.secondary"
        align="center"
        {...props}
      >
        {'Copyright © '}
        <Link color="inherit" href={COMPANY_URL}>
          Sovereign Education Services
        </Link>{' '}
        {new Date().getFullYear()}
        {'.'}
      </Typography>
    );
  }

  function AcceptTerms(props: any) {
    // TODO: update links to policies
    return (
      <Typography {...props}>
        {'By Signing up you accept our '}
        <Link color="inherit" href={COMPANY_URL}>
          Privacy Policy
        </Link>
        {'and our '}
        <Link color="inherit" href={COMPANY_URL}>
          Terms of Service
        </Link>
      </Typography>
    );
  }

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          boxShadow: 3,
          p: 3,
        }}
      >
        <Typography component="h1" variant="h5">
          Sign up
        </Typography>
        {error && (
          <Alert id="alert" severity="error" onClose={() => setError('')} data-testid="signup-error-alert">
            <AlertTitle>Error</AlertTitle>
            {error}
          </Alert>
        )}
        {success && (
          <Alert
            action={
              <Button
                variant="outlined"
                color="inherit"
                size="small"
                onClick={() => navigate(AppRoutes.LOGIN)}
              >
                Login
              </Button>
            }
            data-testid="signup-success-alert"
          >
            <strong>Signup successful</strong>, please login to continue making
            your account.'
          </Alert>
        )}
        {/* add/remove "noValidate" attribute to Box component when testing */}
        {!success && (
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  autoComplete="given-name"
                  name="firstName"
                  required
                  fullWidth
                  id="firstName"
                  label="First Name"
                  type="search"
                  autoFocus
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  inputProps={{ "data-testid": "signup-firstname-input" }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  id="lastName"
                  label="Last Name"
                  name="lastName"
                  type="search"
                  autoComplete="family-name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  inputProps={{ "data-testid": "signup-lastname-input" }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  id="email"
                  type="email"
                  label="Email Address"
                  name="email"
                  autoComplete="email"
                  onChange={(e) => setEmail(e.target.value)}
                  inputProps={{ "data-testid": "signup-email-input" }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  name="password"
                  label="Password"
                  type="password"
                  id="password"
                  autoComplete="new-password"
                  onChange={(e) => setPassword(e.target.value)}
                  inputProps={{ "data-testid": "signup-password-input" }}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox value="marketingEmailOptIn" color="primary" />
                  }
                  label="I want to receive inspiration, marketing, and promotions via email."
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  required={true}
                  checked={agreeToTerms}
                  control={<Checkbox value="acceptTerms" color="primary" inputProps={{ "data-testid": "signup-terms-checkbox" } as any} />}
                  label={<AcceptTerms />}
                  onChange={(event: React.SyntheticEvent) => {
                    setAgreeToTerms(!agreeToTerms);
                    setDisableSubmit(
                      !(event.target as HTMLInputElement).checked
                    );
                  }}
                />
              </Grid>
            </Grid>
            <LoadingButton
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              loading={loading}
              disabled={disabledSubmit}
              data-testid="signup-submit-button"
            >
              Sign Up
            </LoadingButton>
            <Grid container justifyContent="flex-end">
              <Grid item>
                <Link href="/login" variant="body2">
                  Already have an account? Sign in
                </Link>
              </Grid>
            </Grid>
          </Box>
        )}
      </Box>
      <Copyright sx={{ mt: 5 }} />
    </Container>
  );
};

export default Signup;
